import * as THREE from "three";
import { structureColorPalette } from "../../../constants";

const VERTEX_SHADER = `
    // All includes are from Three.js
    // Lambertian reflectance lighting logic from Three.js

    #include <clipping_planes_pars_vertex>
    #ifdef LAMBERT
        #include <common>
        #include <lights_pars_begin>
    #endif

    attribute float highlighted;

    varying float vHighlighted;
    #ifdef LAMBERT
        varying vec3 vLightFront;
        varying vec3 vIndirectFront;
        #ifdef DOUBLE_SIDED
            varying vec3 vLightBack;
            varying vec3 vIndirectBack;
        #endif
    #endif

    void main() {
        #include <begin_vertex>
        #include <project_vertex>
        #include <clipping_planes_vertex>
        #ifdef LAMBERT
            #include <beginnormal_vertex>
            #include <defaultnormal_vertex>
            #include <lights_lambert_vertex>
        #endif

        vHighlighted = highlighted;
    }
`;

const FRAGMENT_SHADER = `
    // All includes are from Three.js
    // Lambertian reflectance lighting logic from Three.js

    #include <clipping_planes_pars_fragment>
    #ifdef LAMBERT
        #include <common>
        #include <lights_pars_begin>
        #include <bsdfs>
    #endif

    uniform float opacity;
    uniform vec3 editColor;
    uniform vec3 highlightColor;

    varying float vHighlighted;
    #ifdef LAMBERT
        varying vec3 vLightFront;
        varying vec3 vIndirectFront;
        #ifdef DOUBLE_SIDED
            varying vec3 vLightBack;
            varying vec3 vIndirectBack;
        #endif
    #endif

    void main() {
        #include <clipping_planes_fragment>

        vec3 diffuse = vHighlighted > 0.25 ? highlightColor : editColor;

        #ifdef LAMBERT
            ReflectedLight reflected_light
                = ReflectedLight(vec3(0), vec3(0), vec3(0), vec3(0));
            #ifdef DOUBLE_SIDED
                reflected_light.indirectDiffuse += gl_FrontFacing
                    ? vIndirectFront
                    : vIndirectBack;
            #else
                reflected_light.indirectDiffuse += vIndirectFront;
            #endif
            reflected_light.indirectDiffuse *= BRDF_Lambert(diffuse);
            #ifdef DOUBLE_SIDED
                reflected_light.directDiffuse = gl_FrontFacing
                    ? vLightFront
                    : vLightBack;
            #else
                reflected_light.directDiffuse = vLightFront;
            #endif
            reflected_light.directDiffuse *= BRDF_Lambert(diffuse);
            vec3 outgoing_light =
                reflected_light.directDiffuse + reflected_light.indirectDiffuse;
        #else
            vec3 outgoing_light = diffuse;
        #endif

        gl_FragColor = vec4(outgoing_light, opacity);
    }
`;

export default class AHMeshEditMaterial extends THREE.ShaderMaterial {
    get opacity() {
        return this.uniforms?.opacity?.value ?? 1;
    }

    set opacity(value) {
        this.uniforms = {
            ...this.uniforms,
            opacity: {
                value,
            },
        };
    }

    constructor(params) {
        super({
            ...params,
            vertexShader: VERTEX_SHADER,
            fragmentShader: FRAGMENT_SHADER,
            transparent: true,
            clipping: true,
            uniforms: THREE.UniformsUtils.merge([
                params?.lights ? THREE.UniformsLib.lights : {},
                {
                    opacity: {
                        value: params?.opacity ?? 1,
                    },
                    editColor: {
                        value: new THREE.Color(
                            structureColorPalette.EDITING_MESH
                        ),
                    },
                    highlightColor: {
                        value: new THREE.Color(
                            structureColorPalette.SELECTING_MESH
                        ),
                    },
                },
            ]),
            defines: {
                LAMBERT: params?.lights ?? false,
            },
        });
    }
}
